import { useCallback, useEffect, useState } from 'react'

const VIRTUAL_AUDIO_DEVICES = ['blackhole', 'soundflower', 'loopback']

function isVirtualAudioDevice(device: MediaDeviceInfo): boolean {
  const label = device.label.toLowerCase()
  return VIRTUAL_AUDIO_DEVICES.some((name) => label.includes(name))
}

function useDeviceList() {
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([])
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([])

  const enumerate = useCallback(async () => {
    const devices = await navigator.mediaDevices.enumerateDevices()
    setAudioDevices(devices.filter((d) => d.kind === 'audioinput' && !isVirtualAudioDevice(d)))
    setVideoDevices(devices.filter((d) => d.kind === 'videoinput'))
  }, [])

  useEffect(() => {
    enumerate()
    navigator.mediaDevices.addEventListener('devicechange', enumerate)
    return () => navigator.mediaDevices.removeEventListener('devicechange', enumerate)
  }, [enumerate])

  return { audioDevices, videoDevices, enumerate }
}

/**
 * Pre-join device management: requests permissions, enumerates devices, auto-selects first available.
 */
export function useMediaDevices() {
  const { audioDevices, videoDevices, enumerate } = useDeviceList()
  const [selectedVideoDevice, setSelectedVideoDevice] = useState('')
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('')

  // Request permissions on mount to get labeled devices, then re-enumerate
  useEffect(() => {
    navigator.mediaDevices
      .getUserMedia({ video: true, audio: true })
      .catch(() => {})
      .then(() => enumerate())
  }, [enumerate])

  // Auto-select first available device
  useEffect(() => {
    const first = audioDevices[0]
    if (!selectedAudioDevice && first) {
      setSelectedAudioDevice(first.deviceId)
    }
  }, [audioDevices, selectedAudioDevice])

  useEffect(() => {
    const first = videoDevices[0]
    if (!selectedVideoDevice && first) {
      setSelectedVideoDevice(first.deviceId)
    }
  }, [videoDevices, selectedVideoDevice])

  return {
    videoDevices,
    audioDevices,
    selectedVideoDevice,
    selectedAudioDevice,
    setSelectedVideoDevice,
    setSelectedAudioDevice,
  }
}
